import type { FormAdditionalSettingsComposed, FormCastService } from '@/services/domain/services/forms/types'
import type FormSubscribeDetails from '@/data/models/domain/services/forms/FormSubscribeDetails'
import type {
  SubscribeAppearance,
  SubscribeForm,
  SubscribeFormComposed,
  SubscribeSettings,
  SubscribeTexts,
} from '@/services/domain/services/forms/subscribe/types'
import type { ModelRaw } from '@/types'

export class SubscribeCastService
  implements FormCastService<FormSubscribeDetails, SubscribeForm, SubscribeFormComposed>
{
  public castFormToComposedForm(form: SubscribeForm): SubscribeFormComposed {
    return this.castFormToComposedFormSettings(form)
  }

  public castEntityToForm(form: FormSubscribeDetails): SubscribeForm {
    return {
      id: form.id,
      name: form.settings.name,
      allowedCountryIds: form.allowedCountryIds,
      defaultCountryId: form.settings.defaultCountryId,

      // landing
      image: form.settings.image,
      imageId: form.settings.imageId,

      ...this.castEntityToTextsForm(form),
      ...this.castEntityToAppearanceForm(form),
      ...this.castEntityToSettingsForm(form),
    }
  }

  public castEntityToTextsForm(form: FormSubscribeDetails): SubscribeTexts & {
    isUserAdditionalConsentFieldVisible: boolean
  } {
    const { settings } = form
    return {
      // texts
      header: settings.title,
      description: settings.description,
      submitText: settings.labelButton,
      legalInformation: settings.additionalText,
      isUserConsentEnabled: settings.isUserConsentEnabled || false,
      privacyPolicyUrl: settings.privacyPolicyUrl || '',
      smsTermsUrl: settings.smsTermsUrl || '',
      successMessage: settings.successMessage,
      additionalConsent: settings.additionalConsent || '',
      isUserAdditionalConsentEnabled: settings.isUserAdditionalConsentEnabled || false,
      isUserAdditionalConsentFieldVisible: !!settings.additionalConsent,

      // fields
      nameLabel: settings.labelName,
      phoneNumberLabel: settings.labelPhone,
    }
  }

  public castEntityToAppearanceForm(form: FormSubscribeDetails): SubscribeAppearance {
    const { settings } = form
    return {
      // colors
      colorTheme: settings.theme,
      fontColor: `#${settings.titleColour}`,
      backgroundColor: `#${settings.descriptionColour}`,
      submitColor: `#${settings.buttonColour}`,

      // dimensions
      width: settings.width,

      // font
      fontFamily: settings.font,
    }
  }

  public castEntityToSettingsForm(form: FormSubscribeDetails): SubscribeSettings {
    const { settings } = form
    return {
      // settings
      countries: form.allowedCountryIds,
      contactList: String(form.widgetGroupId),
      isOptInEnabled: settings.isOptInEnabled || false,
      optInText: settings.optInText || '',
    }
  }

  protected castFormToComposedFormSettings(form: SubscribeForm): SubscribeFormComposed {
    return {
      name: form.name,

      // settings
      allowedCountryIds: form.countries,
      defaultCountryId: form.countries[0] || '',
      widgetGroupId: Number(form.contactList),
      isOptInEnabled: form.isOptInEnabled,
      optInText: form.optInText,

      // texts
      title: form.header,
      description: form.description,
      labelButton: form.submitText,
      additionalText: form.legalInformation,
      isUserConsentEnabled: form.isUserConsentEnabled,
      privacyPolicyUrl: form.privacyPolicyUrl,
      smsTermsUrl: form.smsTermsUrl,
      successMessage: form.successMessage,
      additionalConsent: form.additionalConsent,
      isUserAdditionalConsentEnabled: form.isUserAdditionalConsentEnabled,

      // fields
      labelName: form.nameLabel,
      labelPhone: form.phoneNumberLabel,

      // colors
      theme: form.colorTheme,
      titleColour: form.fontColor.slice(1),
      descriptionColour: form.backgroundColor.slice(1),
      buttonColour: form.submitColor.slice(1),

      // dimensions
      width: form.width,
      height: 400,

      // font
      font: form.fontFamily,

      // landing
      image: form.image,
      imageId: form.imageId,
    }
  }

  public castFormToPartialEntity(form: SubscribeForm): Omit<ModelRaw<FormSubscribeDetails>, 'uniqueCode'> {
    return {
      id: form.id,
      ...this.castFormToComposedFormAdditionalSettings(form),
      settings: this.castFormToComposedForm(form),
    }
  }

  protected castFormToComposedFormAdditionalSettings(form: SubscribeForm): FormAdditionalSettingsComposed {
    return {
      // settings
      allowedCountryIds: form.countries,
      defaultCountryId: form.countries[0] || '',
      widgetGroupId: Number(form.contactList),
    }
  }
}

export default new SubscribeCastService()
